import { ExternalLink } from "lucide-react";

import {
  amplitudeUserUrl,
  cognitoUserUrl,
  sentryIssuesForBusinessUrl,
  sentryIssuesForUserUrl,
} from "@/lib/debug-links";
import { connectedAccountUrl } from "@/lib/stripe-links";

interface DebugLinksRowProps {
  businessId: string;
  /** Cognito sub of the account owner. Absent on merchants with no user yet. */
  primaryUserId: string | undefined;
  stripeConnectedAccountId: string | null | undefined;
}

/**
 * One-click jumps from a merchant to their records in the developer services.
 *
 * Sentry falls back from a per-user search to a business-tagged one when the
 * merchant has no owner yet, because errors during signup happen before there
 * is a user to attribute them to.
 */
export function DebugLinksRow({
  businessId,
  primaryUserId,
  stripeConnectedAccountId,
}: DebugLinksRowProps) {
  const cognitoUrl = primaryUserId ? cognitoUserUrl(primaryUserId) : null;

  const links: { label: string; href: string }[] = [
    ...(primaryUserId
      ? [{ label: "Amplitude", href: amplitudeUserUrl(primaryUserId) }]
      : []),
    {
      label: "Sentry",
      href: primaryUserId
        ? sentryIssuesForUserUrl(primaryUserId)
        : sentryIssuesForBusinessUrl(businessId),
    },
    ...(cognitoUrl ? [{ label: "Cognito", href: cognitoUrl }] : []),
    ...(stripeConnectedAccountId
      ? [
          {
            label: "Stripe",
            href: connectedAccountUrl(stripeConnectedAccountId),
          },
        ]
      : []),
  ];

  return (
    <div className="flex items-center gap-4 border-t px-6 py-2">
      <span className="text-subtle-foreground text-xs font-medium tracking-wide uppercase">
        Debug
      </span>
      {links.map(({ label, href }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground flex items-center gap-1 text-xs hover:text-blue-600 hover:underline"
        >
          {label}
          <ExternalLink className="h-3 w-3" />
        </a>
      ))}
    </div>
  );
}
